import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, Calendar as CalendarIcon, Save, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInYears, parse } from 'date-fns';
import Navigation from '../components/Navigation';
import PhotoUpload from '../components/PhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'photos', or 'preferences'
  
  // Profile data
  const [formData, setFormData] = useState({
    display_name: '',
    birthday: null,
    gender: '',
    city: '',
    state: '',
    location: '',
    travel_distance: 25,
    ghin_number: '',
    ghin_status: 'unknown',
    ghin_handicap_manual: '',
    handicap: '',
    handicap_official: false,
    skill_level: '',
    is_private_club_member: false,
    private_club_name: '',
    bio: '',
    profile_photo: '',
    preferred_tee_times: [],
    looking_for: 'playing_partner',
    group_size: 1,
    preferred_group_sizes: [],
    playing_pace: '',
    walking_or_riding: 'either',
    plays_music: false,
    drinks_on_course: '',
    smokes_on_course: '',
    follows_golf: null,
    plays_for_money: null,
    availability: [],
  });

  // Match preferences
  const [preferences, setPreferences] = useState({
    gender_preference: 'any',
    min_age: 18,
    max_age: 80,
    min_handicap: 0,
    max_handicap: 36,
    must_have_handicap: false,
    prefer_private_club_members: false,
    preferred_group_sizes: [1, 2, 3],
    pref_pace: 'any',
    pref_music: 'any',
    pref_drinking: 'any',
    pref_smoking: 'any',
    pref_walking: 'any',
    pref_gambling: 'any',
    pref_follows_golf: 'any',
    pref_availability: [],
  });

  // Load existing user data
  useEffect(() => {
    if (user) {
      // Parse birthday from age if we have age but no birthday
      let birthday = null;
      if (user.birthday) {
        birthday = new Date(user.birthday);
      } else if (user.age) {
        // Estimate birthday from age (use Jan 1 of birth year)
        const birthYear = new Date().getFullYear() - user.age;
        birthday = new Date(birthYear, 0, 1);
      }

      setFormData({
        display_name: user.display_name || '',
        birthday: birthday,
        gender: user.gender || '',
        city: user.city || (user.location ? user.location.split(',')[0].trim() : ''),
        state: user.state || (user.location && user.location.includes(',') ? user.location.split(',')[1].trim() : ''),
        location: user.location || '',
        travel_distance: user.travel_distance || 25,
        ghin_number: user.ghin_number || '',
        ghin_status: user.ghin_status || 'unknown',
        ghin_handicap_manual: user.ghin_handicap_manual != null ? user.ghin_handicap_manual.toString() : '',
        handicap: user.handicap?.toString() || '',
        handicap_official: user.handicap_official || false,
        skill_level: user.skill_level || '',
        is_private_club_member: user.is_private_club_member || false,
        private_club_name: user.private_club_name || '',
        bio: user.bio || '',
        profile_photo: user.profile_photo || '',
        preferred_tee_times: user.preferred_tee_times || [],
        looking_for: user.looking_for || 'playing_partner',
        group_size: user.group_size || 1,
        preferred_group_sizes: user.preferred_group_sizes || [],
        playing_pace: user.playing_pace || '',
        walking_or_riding: user.walking_or_riding || 'either',
        plays_music: user.plays_music || false,
        drinks_on_course: user.drinks_on_course || '',
        smokes_on_course: user.smokes_on_course || '',
        follows_golf: user.follows_golf ?? null,
        plays_for_money: user.plays_for_money ?? null,
        availability: user.availability || [],
      });

      setPreferences({
        gender_preference: user.gender_preference || 'any',
        min_age: user.min_age || 18,
        max_age: user.max_age || 80,
        min_handicap: user.min_handicap || 0,
        max_handicap: user.max_handicap || 36,
        must_have_handicap: user.must_have_handicap || false,
        prefer_private_club_members: user.prefer_private_club_members || false,
        preferred_group_sizes: user.preferred_group_sizes || [1, 2, 3],
        pref_pace: user.pref_pace || 'any',
        pref_music: user.pref_music || 'any',
        pref_drinking: user.pref_drinking || 'any',
        pref_smoking: user.pref_smoking || 'any',
        pref_walking: user.pref_walking || 'any',
        pref_gambling: user.pref_gambling || 'any',
        pref_follows_golf: user.pref_follows_golf || 'any',
        pref_availability: user.pref_availability || [],
      });
    }
  }, [user]);

  const calculateAge = (birthday) => {
    if (!birthday) return null;
    return differenceInYears(new Date(), birthday);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
  };

  const toggleTeeTime = (time) => {
    setFormData(prev => ({
      ...prev,
      preferred_tee_times: prev.preferred_tee_times.includes(time)
        ? prev.preferred_tee_times.filter(t => t !== time)
        : [...prev.preferred_tee_times, time]
    }));
  };

  // Refresh user data after photo changes
  const refreshUserData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      updateUser(response.data);
    } catch (error) {
      /* silent */
    }
  }, [updateUser]);

  const handleSave = async () => {
    if (!formData.display_name || !formData.birthday || !formData.gender || !(formData.city && formData.state)) {
      toast.error('Please fill in all required fields (name, birthday, gender, city & state)');
      return;
    }

    setLoading(true);
    try {
      const location = `${formData.city}, ${formData.state}`;
      const submitData = {
        ...formData,
        ...preferences,
        location,
        age: calculateAge(formData.birthday),
        birthday: formData.birthday?.toISOString(),
        handicap: formData.handicap ? parseFloat(formData.handicap) : null,
        ghin_handicap_manual: formData.ghin_handicap_manual === '' ? null : parseFloat(formData.ghin_handicap_manual)
      };
      delete submitData.city;
      delete submitData.state;

      const response = await axios.put(`${API}/profile`, submitData);
      updateUser(response.data);
      toast.success('Profile updated successfully!');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <Navigation />

      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-xl border-b border-border px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/settings')}
              className="rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-serif text-xl font-semibold">Edit Profile</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'profile' ? 'default' : 'outline'}
            onClick={() => setActiveTab('profile')}
            className="flex-1 rounded-xl text-sm"
            data-testid="tab-profile"
          >
            Profile
          </Button>
          <Button
            variant={activeTab === 'photos' ? 'default' : 'outline'}
            onClick={() => setActiveTab('photos')}
            className="flex-1 rounded-xl text-sm"
            data-testid="tab-photos"
          >
            <Camera className="w-4 h-4 mr-1" />
            Photos
          </Button>
          <Button
            variant={activeTab === 'preferences' ? 'default' : 'outline'}
            onClick={() => setActiveTab('preferences')}
            className="flex-1 rounded-xl text-sm"
            data-testid="tab-preferences"
          >
            Preferences
          </Button>
        </div>

        {activeTab === 'profile' ? (
          <div className="space-y-6">
            {/* Basic Info */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Basic Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name *</Label>
                  <Input
                    id="display_name"
                    placeholder="How should others see you?"
                    value={formData.display_name}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                    className="h-12 rounded-xl"
                    data-testid="display-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Birthday *</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={formData.birthday ? String(formData.birthday.getMonth() + 1) : ''} onValueChange={v => {
                      const d = formData.birthday || new Date(1990, 0, 1);
                      handleChange('birthday', new Date(d.getFullYear(), parseInt(v) - 1, d.getDate()));
                    }}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="birth-month-select"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                          <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={formData.birthday ? String(formData.birthday.getDate()) : ''} onValueChange={v => {
                      const d = formData.birthday || new Date(1990, 0, 1);
                      handleChange('birthday', new Date(d.getFullYear(), d.getMonth(), parseInt(v)));
                    }}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="birth-day-select"><SelectValue placeholder="Day" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 31}, (_, i) => (
                          <SelectItem key={i+1} value={String(i + 1)}>{i + 1}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={formData.birthday ? String(formData.birthday.getFullYear()) : ''} onValueChange={v => {
                      const d = formData.birthday || new Date(1990, 0, 1);
                      handleChange('birthday', new Date(parseInt(v), d.getMonth(), d.getDate()));
                    }}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="birth-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 70}, (_, i) => {
                          const yr = new Date().getFullYear() - 16 - i;
                          return <SelectItem key={yr} value={String(yr)}>{yr}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.birthday && (
                    <p className="text-sm text-muted-foreground">
                      {calculateAge(formData.birthday)} years old
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Gender *</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell others about yourself and your golf game..."
                    value={formData.bio}
                    onChange={(e) => handleChange('bio', e.target.value)}
                    className="min-h-24 rounded-xl"
                    data-testid="bio-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Location</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="e.g., Tampa"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      onBlur={async (e) => {
                        const city = e.target.value;
                        if (city && city.length >= 2 && !formData.state) {
                          try {
                            const res = await axios.get(`${API}/golf/geocode-city`, { params: { city } });
                            if (res.data.state) handleChange('state', res.data.state);
                          } catch {}
                        }
                      }}
                      className="h-12 rounded-xl"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select value={formData.state || ''} onValueChange={v => handleChange('state', v)}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="state-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {[
                          'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
                          'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
                          'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
                        ].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Travel Distance</Label>
                    <span className="text-sm font-medium text-primary">{formData.travel_distance} miles</span>
                  </div>
                  <Slider
                    value={[formData.travel_distance]}
                    onValueChange={([v]) => handleChange('travel_distance', v)}
                    max={100}
                    min={5}
                    step={5}
                    className="py-4"
                    data-testid="distance-slider"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Golf Details */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Golf Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>GHIN Status</Label>
                  <Select value={formData.ghin_status} onValueChange={(v) => handleChange('ghin_status', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="ghin-status-select">
                      <SelectValue placeholder="Do you have a GHIN?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="verified">Yes, I have a GHIN number</SelectItem>
                      <SelectItem value="estimated">No, but I know my estimated handicap</SelectItem>
                      <SelectItem value="unknown">Unknown / New to golf</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.ghin_status === 'verified' && (
                  <div className="space-y-2">
                    <Label htmlFor="ghin_number">GHIN Number</Label>
                    <Input
                      id="ghin_number"
                      placeholder="Enter your GHIN number"
                      value={formData.ghin_number}
                      onChange={(e) => handleChange('ghin_number', e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="ghin-number-input"
                    />
                  </div>
                )}

                {/* GHIN Handicap Index® input hidden in v1.0 — USGA GPA integration
                    lands in v1.1 with proper API import + trademark treatment.
                    Field kept in the model so existing data isn't lost. */}
                {false && (formData.ghin_status === 'verified' || formData.ghin_status === 'estimated') && (
                  <div className="space-y-2">
                    <Label htmlFor="ghin_handicap_manual">Official GHIN Handicap (optional)</Label>
                    <Input
                      id="ghin_handicap_manual"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 12.4"
                      value={formData.ghin_handicap_manual ?? ''}
                      onChange={(e) => handleChange('ghin_handicap_manual', e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="ghin-handicap-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Coming in v1.1 — direct GHIN import via USGA GPA integration.
                    </p>
                  </div>
                )}

                {(formData.ghin_status === 'verified' || formData.ghin_status === 'estimated') && (
                  <div className="space-y-2">
                    <Label htmlFor="handicap">Estimated Handicap (legacy field)</Label>
                    <Input
                      id="handicap"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 12.4"
                      value={formData.handicap}
                      onChange={(e) => handleChange('handicap', e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="handicap-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your Strokes to Par (a personal recreational metric, not a handicap) is calculated automatically from your posted rounds and shown on your profile.
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <div>
                    <Label className="text-base">Private Club Member?</Label>
                    <p className="text-sm text-muted-foreground">Are you a member of a private club?</p>
                  </div>
                  <Switch
                    checked={formData.is_private_club_member}
                    onCheckedChange={(v) => handleChange('is_private_club_member', v)}
                    data-testid="private-club-switch"
                  />
                </div>

                {formData.is_private_club_member && (
                  <div className="space-y-2">
                    <Label htmlFor="private_club_name">Club Name</Label>
                    <Input
                      id="private_club_name"
                      placeholder="Name of your club"
                      value={formData.private_club_name}
                      onChange={(e) => handleChange('private_club_name', e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="club-name-input"
                    />
                  </div>
                )}

                <div className="space-y-3">
                  <Label>Preferred Tee Times</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {['morning', 'afternoon', 'evening', 'weekend'].map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => toggleTeeTime(time)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium capitalize transition-colors ${
                          formData.preferred_tee_times.includes(time)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`tee-time-${time}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <div>
                    <Label className="text-base">Official Handicap?</Label>
                    <p className="text-sm text-muted-foreground">Is this a GHIN/official index?</p>
                  </div>
                  <Switch
                    checked={formData.handicap_official}
                    onCheckedChange={(v) => handleChange('handicap_official', v)}
                    data-testid="handicap-official-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* On Course Preferences */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">On Course</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Playing Pace</Label>
                  <Select value={formData.playing_pace} onValueChange={(v) => handleChange('playing_pace', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="pace-select"><SelectValue placeholder="How do you play?" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ready_golf">Ready Golf - I play when ready</SelectItem>
                      <SelectItem value="quick">Quick to the Ball - Minimal delay</SelectItem>
                      <SelectItem value="average">Average Pace - Standard rhythm</SelectItem>
                      <SelectItem value="deliberate">Takes My Time - Reads every putt</SelectItem>
                      <SelectItem value="very_deliberate">Very Deliberate - Full routine, every shot</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Walking or Riding?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'walk',l:'Walk'},{v:'ride',l:'Cart'},{v:'either',l:'Either'}].map(o => (
                      <button key={o.v} type="button" onClick={() => handleChange('walking_or_riding', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${formData.walking_or_riding === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}
                        data-testid={`ride-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <Label className="text-base">Listen to music on course?</Label>
                  <Switch checked={formData.plays_music} onCheckedChange={(v) => handleChange('plays_music', v)} data-testid="music-switch" />
                </div>

                <div className="space-y-2">
                  <Label>Drinking on course?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'yes',l:'Yes'},{v:'sometimes',l:'Sometimes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button" onClick={() => handleChange('drinks_on_course', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${formData.drinks_on_course === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}>{o.l}</button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Smoking on course?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'yes',l:'Yes'},{v:'sometimes',l:'Sometimes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button" onClick={() => handleChange('smokes_on_course', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${formData.smokes_on_course === o.v ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}>{o.l}</button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <Label className="text-base">Follow golf / watch tours?</Label>
                  <Switch checked={formData.follows_golf === true} onCheckedChange={(v) => handleChange('follows_golf', v)} data-testid="follows-golf-switch" />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                  <Label className="text-base">Enjoy friendly betting?</Label>
                  <Switch checked={formData.plays_for_money === true} onCheckedChange={(v) => handleChange('plays_for_money', v)} data-testid="gambling-switch" />
                </div>

                <div className="space-y-2">
                  <Label>Availability</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{v:'weekday_mornings',l:'Weekday AM'},{v:'weekday_afternoons',l:'Weekday PM'},{v:'weekend_mornings',l:'Weekend AM'},{v:'weekend_afternoons',l:'Weekend PM'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handleChange('availability', formData.availability.includes(o.v) ? formData.availability.filter(a => a !== o.v) : [...formData.availability, o.v])}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${formData.availability.includes(o.v) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/50'}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What I'm Looking For */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">What I'm Looking For</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>I'm Looking For</Label>
                  <Select value={formData.looking_for} onValueChange={(v) => handleChange('looking_for', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="looking-for-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="playing_partner">Playing Partner</SelectItem>
                      <SelectItem value="group_fill">Group to Join</SelectItem>
                      <SelectItem value="club_member">Club Members to Recruit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Current Group Size</Label>
                  <Select value={String(formData.group_size)} onValueChange={(v) => handleChange('group_size', parseInt(v))}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="group-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Single</SelectItem>
                      <SelectItem value="2">Duo</SelectItem>
                      <SelectItem value="3">Threesome</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'photos' ? (
          /* Photos Tab */
          <div className="space-y-6">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Your Photos</CardTitle>
                <CardDescription>
                  Add up to 8 photos. Your first photo will be your main profile picture.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PhotoUpload
                  photos={user?.photos || []}
                  profilePhoto={user?.profile_photo}
                  onUpdate={refreshUserData}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Match Preferences Tab — Mirrors the Profile page format */
          <div className="space-y-6">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">Who Do You Want to See?</CardTitle>
                <CardDescription>
                  Set your preferences to filter golfers in Discover. These mirror your profile so you can match on what matters.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Gender Preference */}
                <div className="space-y-2">
                  <Label>Show Me</Label>
                  <Select value={preferences.gender_preference} onValueChange={(v) => handlePreferenceChange('gender_preference', v)}>
                    <SelectTrigger className="h-12 rounded-xl" data-testid="gender-pref-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Everyone</SelectItem>
                      <SelectItem value="male">Men Only</SelectItem>
                      <SelectItem value="female">Women Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Age Range */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Age Range</Label>
                    <span className="text-sm font-medium text-primary">
                      {preferences.min_age} - {preferences.max_age} years
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-12">Min:</span>
                      <Slider
                        value={[preferences.min_age]}
                        onValueChange={([v]) => handlePreferenceChange('min_age', Math.min(v, preferences.max_age - 1))}
                        max={80}
                        min={18}
                        step={1}
                        className="flex-1"
                        data-testid="min-age-slider"
                      />
                      <span className="text-sm font-medium w-8">{preferences.min_age}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-12">Max:</span>
                      <Slider
                        value={[preferences.max_age]}
                        onValueChange={([v]) => handlePreferenceChange('max_age', Math.max(v, preferences.min_age + 1))}
                        max={80}
                        min={18}
                        step={1}
                        className="flex-1"
                        data-testid="max-age-slider"
                      />
                      <span className="text-sm font-medium w-8">{preferences.max_age}</span>
                    </div>
                  </div>
                </div>

                {/* Handicap Range */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Handicap Range</Label>
                    <span className="text-sm font-medium text-primary">
                      {preferences.min_handicap} - {preferences.max_handicap}
                    </span>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-12">Min:</span>
                      <Slider
                        value={[preferences.min_handicap]}
                        onValueChange={([v]) => handlePreferenceChange('min_handicap', Math.min(v, preferences.max_handicap - 1))}
                        max={36}
                        min={0}
                        step={1}
                        className="flex-1"
                        data-testid="min-handicap-slider"
                      />
                      <span className="text-sm font-medium w-8">{preferences.min_handicap}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-12">Max:</span>
                      <Slider
                        value={[preferences.max_handicap]}
                        onValueChange={([v]) => handlePreferenceChange('max_handicap', Math.max(v, preferences.min_handicap + 1))}
                        max={36}
                        min={0}
                        step={1}
                        className="flex-1"
                        data-testid="max-handicap-slider"
                      />
                      <span className="text-sm font-medium w-8">{preferences.max_handicap}</span>
                    </div>
                  </div>
                </div>

                {/* Additional Filters */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                    <div>
                      <Label className="text-base">Must Have Handicap</Label>
                      <p className="text-sm text-muted-foreground">Only show golfers with a handicap listed</p>
                    </div>
                    <Switch
                      checked={preferences.must_have_handicap}
                      onCheckedChange={(v) => handlePreferenceChange('must_have_handicap', v)}
                      data-testid="must-have-handicap-switch"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                    <div>
                      <Label className="text-base">Prefer Club Members</Label>
                      <p className="text-sm text-muted-foreground">Prioritize private club members</p>
                    </div>
                    <Switch
                      checked={preferences.prefer_private_club_members}
                      onCheckedChange={(v) => handlePreferenceChange('prefer_private_club_members', v)}
                      data-testid="prefer-club-members-switch"
                    />
                  </div>
                </div>

                {/* Group Size Preference */}
                <div className="space-y-3 pt-4">
                  <Label>Show Me Groups Of</Label>
                  <p className="text-sm text-muted-foreground">Select which group sizes you want to see</p>
                  <div className="flex gap-3">
                    {[
                      { value: 1, label: 'Single' },
                      { value: 2, label: 'Duo' },
                      { value: 3, label: 'Threesome' }
                    ].map((size) => (
                      <Button
                        key={size.value}
                        variant={preferences.preferred_group_sizes?.includes(size.value) ? 'default' : 'outline'}
                        className="flex-1 rounded-xl h-12"
                        onClick={() => {
                          const current = preferences.preferred_group_sizes || [1, 2, 3];
                          const newSizes = current.includes(size.value)
                            ? current.filter(s => s !== size.value)
                            : [...current, size.value];
                          if (newSizes.length > 0) {
                            handlePreferenceChange('preferred_group_sizes', newSizes);
                          }
                        }}
                        data-testid={`group-size-pref-${size.value}`}
                      >
                        {size.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* On Course Preferences — MIRRORS the Profile "On Course" section */}
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-lg">On Course Preferences</CardTitle>
                <CardDescription>What do you prefer in a playing partner? "Any" means no filter.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Playing Pace */}
                <div className="space-y-2">
                  <Label>Playing Pace</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: 'any', l: 'Any' },
                      { v: 'ready_golf', l: 'Ready Golf' },
                      { v: 'quick', l: 'Quick' },
                      { v: 'average', l: 'Average' },
                      { v: 'deliberate', l: 'Deliberate' },
                      { v: 'very_deliberate', l: 'Very Deliberate' },
                    ].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_pace', o.v)}
                        className={`p-3 rounded-xl border-2 text-xs font-medium transition-colors ${
                          (preferences.pref_pace || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-pace-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Walking or Riding */}
                <div className="space-y-2">
                  <Label>Walking or Riding?</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[{v:'any',l:'Any'},{v:'walk',l:'Walk'},{v:'ride',l:'Cart'},{v:'either',l:'Either'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_walking', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                          (preferences.pref_walking || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-walk-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Music on course */}
                <div className="space-y-2">
                  <Label>Listen to music on course?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'any',l:'Any'},{v:'yes',l:'Yes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_music', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                          (preferences.pref_music || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-music-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Drinking on course */}
                <div className="space-y-2">
                  <Label>Drinking on course?</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[{v:'any',l:'Any'},{v:'yes',l:'Yes'},{v:'sometimes',l:'Sometimes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_drinking', o.v)}
                        className={`p-3 rounded-xl border-2 text-xs font-medium transition-colors ${
                          (preferences.pref_drinking || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-drink-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Smoking on course */}
                <div className="space-y-2">
                  <Label>Smoking on course?</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[{v:'any',l:'Any'},{v:'yes',l:'Yes'},{v:'sometimes',l:'Sometimes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_smoking', o.v)}
                        className={`p-3 rounded-xl border-2 text-xs font-medium transition-colors ${
                          (preferences.pref_smoking || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-smoke-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Follow golf / watch tours */}
                <div className="space-y-2">
                  <Label>Follow golf / watch tours?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'any',l:'Any'},{v:'yes',l:'Yes'},{v:'no',l:'No'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_follows_golf', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                          (preferences.pref_follows_golf || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-golf-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Friendly betting */}
                <div className="space-y-2">
                  <Label>Friendly betting?</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[{v:'any',l:'No Preference'},{v:'yes',l:'Likes to Bet'},{v:'no',l:'No Betting'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => handlePreferenceChange('pref_gambling', o.v)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                          (preferences.pref_gambling || 'any') === o.v
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-gamble-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-2">
                  <Label>Availability</Label>
                  <p className="text-sm text-muted-foreground">Show golfers available at these times</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[{v:'weekday_mornings',l:'Weekday AM'},{v:'weekday_afternoons',l:'Weekday PM'},{v:'weekend_mornings',l:'Weekend AM'},{v:'weekend_afternoons',l:'Weekend PM'}].map(o => (
                      <button key={o.v} type="button"
                        onClick={() => {
                          const curr = preferences.pref_availability || [];
                          handlePreferenceChange('pref_availability', curr.includes(o.v) ? curr.filter(a => a !== o.v) : [...curr, o.v]);
                        }}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                          (preferences.pref_availability || []).includes(o.v)
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`pref-avail-${o.v}`}
                      >{o.l}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-sm text-muted-foreground text-center px-4">
              These preferences will filter who appears in your Discover feed
            </p>
          </div>
        )}

        {/* Extra padding at bottom for navigation */}
        <div className="h-8" />
      </div>
    </div>
  );
};

export default ProfileEdit;
